import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence, useDragControls, useReducedMotion } from "framer-motion";
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from "../../utils/sheetMotion";
import { useNavigate } from "react-router-dom";
import api from "../../api/apiHelper";
import { readApiCache, writeApiCache } from '../../utils/apiCache';
import Page from "../molecules/Page";
import { RenderMarkdownWithLatex } from "../../utils/utils"; 
import SurefirePaywallPopup from "../molecules/SurefirePaywallPopup";
import SessionSetupPopup from "../molecules/SessionSetupPopup";
import { FaGraduationCap, FaLock } from "react-icons/fa";
import {
  FiCheckCircle,
  FiClock,
  FiAlertTriangle,
  FiZap,
  FiInfo,
  FiChevronRight,
  FiChevronDown,
  FiPlay,
  FiActivity,
  FiLayers,
  FiFolder,
  FiCpu,
  FiTarget,
  FiX,

  FiPackage
} from 'react-icons/fi';

// ==============================================================================
// 1. KONFIGURACJA
// ==============================================================================
const STATUS_HINTS = {
  MASTERED: "Ten temat siedzi w Twojej pamięci długotrwałej. Możesz go powtórzyć w dowolnym momencie.",
  DECAYING: "Dawno nie ćwiczono tego tematu i wiedza zaczyna zanikać. Rozwiąż zadanie, żeby ją odświeżyć!",
  WAITING: "Twój mózg właśnie konsoliduje wiedzę. Przerwa między fazami jest kluczowa.",
  PHASE1_IN_PROGRESS: "Faza 1: uczysz się rozwiązywać ten typ zadań. Zrób 3 zadania poprawnie.",
  PHASE2_READY: "Faza 1 zaliczona! Czas na utrwalenie — rozwiąż poprawnie 2 zadania, żeby temat trafił do pamięci długotrwałej.",
  NOT_STARTED: "Jeszcze nie ruszano tego tematu. Kliknij Start, żeby zacząć od pierwszego zadania.",
};

const SECTION_CONFIG = {
  decaying:      { label: "Do powtórki",           description: "Wymagają powtórki",         icon: <FiAlertTriangle />, colorClass: "text-[#8A0000]", dotClass: "bg-[#8A0000]" },
  consolidation: { label: "Do utrwalenia",          description: "W trakcie utrwalania",      icon: <FiZap />,           colorClass: "text-[#E09F2E]", dotClass: "bg-[#E09F2E]" },
  learning:      { label: "W trakcie nauki",        description: "Aktywnie ćwiczone",         icon: <FiActivity />,      colorClass: "text-[#233855]", dotClass: "bg-[#233855]" },
  waiting:       { label: "Odpoczywają (Pauza)",    description: "Czekają na kolejną sesję",  icon: <FiClock />,         colorClass: "text-gray-400",  dotClass: "bg-gray-300"  },
  mastered:      { label: "W pamięci długotrwałej", description: "Opanowane",                 icon: <FiCheckCircle />,   colorClass: "text-[#007D00]", dotClass: "bg-[#007D00]" },
  not_started:   { label: "Do zrobienia",           description: "Jeszcze nierozpoczęte",     icon: <FiLayers />,        colorClass: "text-gray-400",  dotClass: "bg-gray-200"  },
};

const LOADING_STEPS = ['rozwiązujemy zadania', 'przeszukujemy kartę wzorów CKE', 'przygotowujemy Orła AI'];

const pluralPewniak = (n) => {
  if (n === 1) return 'Pewniaka';
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return 'Pewniaki';
  return 'Pewniaków';
};


// ==============================================================================
// 2. KOMPONENTY UI
// ==============================================================================
const SurefireBenefits = () => (
  <div className="flex flex-col gap-2.5 mb-5">
    <div className="flex items-center gap-2.5">
      <FiCpu className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">34-46% na maturze:</strong> Typy zadań, które CKE daje najczęściej.</p>
    </div>
    <div className="flex items-center gap-2.5">
      <FiTarget className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Pamięć długotrwała:</strong> Nie zapomnisz na maturze.</p>
    </div>
    {/* <div className="flex items-center gap-2.5">
      <FiCheckCircle className="text-[#E09F2E]/60 flex-shrink-0" size={13} />
      <p className="text-sm text-gray-500 leading-snug"><strong className="text-gray-700 font-semibold">Baza:</strong> Umiejętności potrzebne do trudniejszych zadań.</p>
    </div> */}
  </div>
);

const PhaseLabel = ({ status, progress }) => {
  const config = {
    PHASE1_IN_PROGRESS: { text: `Faza 1 · ${progress}/3`,     color: "text-[#233855] bg-[#233855]/8 border-[#233855]/15" },
    PHASE2_READY:       { text: "Faza 2 — do utrwalenia",     color: "text-[#E09F2E] bg-[#E09F2E]/10 border-[#E09F2E]/20" },
    WAITING:            { text: "Mózg konsoliduje",            color: "text-gray-400 bg-gray-100 border-gray-200" },
    MASTERED:           { text: "Opanowane",                   color: "text-[#007D00] bg-[#007D00]/8 border-[#007D00]/20" },
    DECAYING:           { text: "Wiedza zanika!",              color: "text-[#8A0000] bg-[#8A0000]/8 border-[#8A0000]/15" },
    NOT_STARTED:        { text: "Nierozpoczęte",               color: "text-gray-400 bg-gray-100 border-gray-200" },
  };
  const c = config[status] || config.NOT_STARTED;
  return (
    <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full border mt-2 ${c.color}`}>
      {c.text}
    </span>
  );
};

const ProgressTrack = ({ progress, status }) => {
  const isDecaying = status === 'DECAYING';
  const isMastered = status === 'MASTERED';

  const getStepStyle = (index) => {
    const isDone = progress > index;
    const isCurrent = progress === index;
    if (isDone) {
      if (isDecaying) return "bg-[#8A0000] border-[#8A0000]";
      if (isMastered) return "bg-[#007D00] border-[#007D00]";
      return "bg-[#E09F2E] border-[#E09F2E]";
    }
    if (isCurrent) return "bg-[#E09F2E]/20 border-[#E09F2E] border-2";
    return "bg-white border-gray-200 border";
  };

  return (
    <div className="flex items-center gap-1.5 mt-2">
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-200 ${getStepStyle(i)}`}>
            {progress > i && <FiCheckCircle className="text-white" size={9} strokeWidth={3} />}
          </div>
        ))}
      </div>
      <div className="w-1 h-1 rounded-full bg-gray-200 mx-0.5" />
      <div className="flex items-center gap-1">
        {[3, 4].map((i) => (
          <div key={i} className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-200 ${getStepStyle(i)}`}>
            {progress > i && <FiCheckCircle className="text-white" size={9} strokeWidth={3} />}
          </div>
        ))}
      </div>
    </div>
  );
};

const PhaseIndicator = ({ status, progress }) => {
  if (status === 'PHASE1_IN_PROGRESS' || status === 'PHASE2_READY') {
    return <ProgressTrack progress={progress} status={status} />;
  }
  if (status === 'DECAYING' || status === 'MASTERED') return null;
  return <PhaseLabel status={status} progress={progress} />;
};

const DOT_STATUS_COLOR = {
  MASTERED:          'bg-[#007D00] border-[#007D00]',
  DECAYING:          'bg-[#8A0000] border-[#8A0000]',
  PHASE2_READY:      'bg-[#E09F2E] border-[#E09F2E]',
  PHASE1_IN_PROGRESS:'bg-[#233855]/60 border-[#233855]/60',
  WAITING:           'bg-gray-300 border-gray-300',
  NOT_STARTED:       'bg-gray-200 border-gray-200',
};


const ROADMAP_SECTIONS = [
  { type: 'mastered',     statusFilter: ['MASTERED'] },
  { type: 'decaying',     statusFilter: ['DECAYING'] },
  { type: 'consolidation',statusFilter: ['PHASE2_READY'] },
  { type: 'learning',     statusFilter: ['PHASE1_IN_PROGRESS'] },
  { type: 'waiting',      statusFilter: ['WAITING'] },
  { type: 'not_started',  statusFilter: ['NOT_STARTED'] },
];

const PewniakiRoadmapModal = ({ isOpen, onClose, surefires, onStart }) => {
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('bottomsheet:open'));
      setExpandedId(null);
      return () => window.dispatchEvent(new CustomEvent('bottomsheet:close'));
    }
  }, [isOpen]);

  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(isOpen);
  const masteredCount = surefires.filter(s => s.status === 'MASTERED').length;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="pew-backdrop"
            {...getSheetBackdropMotion()}
            className="fixed inset-0 bg-black/50 z-[9990]"
            onClick={onClose}
          />
          <motion.div
            key="pew-sheet"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
          >
            <div
              className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 pt-2 pb-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="font-apotek text-lg text-[#233855] tracking-wide uppercase leading-none">Kolekcja Pewniaków</h2>
                <p className="text-xs text-gray-400 mt-0.5">{masteredCount} z {surefires.length} opanowanych</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <FiX size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-6">
          {ROADMAP_SECTIONS.map(({ type, statusFilter }) => {
            const items = surefires.filter(s => statusFilter.includes(s.status));
            if (items.length === 0) return null;
            const cfg = SECTION_CONFIG[type];
            const isDim = type === 'not_started';
            const isMastered = type === 'mastered';

            if (isMastered) {
              return (
                <div key={type} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0 bg-[#007D00]" />
                    <span className="text-xs font-bold uppercase tracking-widest text-[#007D00]">{cfg.label}</span>
                    <span className="text-[11px] text-gray-300 font-medium">{items.length}</span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map(item => {
                      const isExpanded = expandedId === item.id;
                      return (
                        <motion.div
                          key={item.id}
                          layout
                          className={isExpanded ? 'col-span-2' : ''}
                          transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                        >
                        <motion.button
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          whileTap={{ scale: 0.94 }}
                          className={`w-full relative overflow-hidden text-left rounded-xl border transition-colors ${
                            isExpanded
                              ? 'p-4 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)]'
                              : 'p-3 bg-white hover:shadow-sm'
                          }`}
                          style={{ borderColor: '#007D00' }}
                        >
                          {!isExpanded && (
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-sheen pointer-events-none" />
                          )}
                          {isExpanded ? (
                            <div>
                              <div className="flex items-center gap-2.5 mb-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#233855] flex-shrink-0">
                                  <FiCheckCircle size={15} className="text-[#E09F2E]" strokeWidth={2.5} />
                                </div>
                                <p className="text-sm font-semibold text-[#233855] leading-snug">{item.title}</p>
                              </div>
                              {item.field && (
                                <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                                  <FiFolder size={9} /><span>{item.field}</span>
                                </div>
                              )}
                              {(item.points || (item.sources && item.sources.length > 0)) && (
                                <div className="flex flex-wrap items-center gap-1 mb-2.5">
                                  {item.points && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold">
                                      {String(item.points).replace(' pkt', '')} pkt
                                    </span>
                                  )}
                                  {item.sources && item.sources.map((src, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500">
                                      <FaGraduationCap className="text-[#233855]" size={7} />
                                      {src.replace('Zadanie', 'Zad.')}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {item.exampleProblem && (
                                <div className="mb-3">
                                  <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-1 flex items-center gap-1">
                                    <FaGraduationCap size={8} /> Przykładowe zadanie
                                  </p>
                                  <div className="text-sm text-gray-600 rounded-lg p-2.5 border bg-white border-gray-100">
                                    <RenderMarkdownWithLatex text={item.exampleProblem} />
                                  </div>
                                </div>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); onClose(); onStart(item); }}
                                className="w-full py-1.5 rounded-lg text-xs font-bold font-apotek tracking-wider uppercase active:scale-[0.98] transition-all border border-[#007D00]/30 text-[#007D00] bg-transparent hover:bg-[#007D00]/5"
                              >
                                Powtórz
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center text-center">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 bg-[#233855] shadow-[0_4px_14px_-2px_rgba(35,56,85,0.45)]">
                                <FiCheckCircle size={18} className="text-[#E09F2E]" strokeWidth={2.5} />
                              </div>
                              <p className="text-[13px] font-medium text-gray-700 leading-tight line-clamp-2">{item.title}</p>
                            </div>
                          )}
                        </motion.button>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <div key={type} className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dotClass}`} />
                  <span className={`text-xs font-bold uppercase tracking-widest ${cfg.colorClass}`}>{cfg.label}</span>
                  <span className="text-[11px] text-gray-300 font-medium">{items.length}</span>
                  <div className="flex-1 h-px bg-gray-100" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {items.map(item => {
                    const isExpanded = expandedId === item.id;
                    const expandedBg =
                      type === 'decaying'
                      ? 'bg-gradient-to-br from-[#8A0000]/[0.04] to-transparent border-[#8A0000]/20'
                      : 'bg-white border-gray-200';

                    const actionBtn = (() => {
                      if (type === 'waiting') return null;
                      if (type === 'decaying') return { label: 'Napraw teraz', cls: 'bg-[#8A0000] text-white hover:bg-[#8A0000]/90' };
                      if (type === 'consolidation') return { label: 'Utrwal', cls: 'bg-[#E09F2E] text-white hover:bg-[#E09F2E]/90' };
                      return { label: 'Start', cls: 'bg-[#233855] text-white hover:bg-[#233855]/90' };
                    })();

                    return (
                      <motion.div
                        key={item.id}
                        layout
                        className={isExpanded ? 'col-span-2' : ''}
                        transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                      >
                        <motion.button
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          whileTap={{ scale: 0.94 }}
                          className={`w-full relative overflow-hidden text-left rounded-xl border transition-colors ${
                            isExpanded
                              ? `p-4 ${expandedBg}`
                              : isDim
                              ? 'p-3 bg-gray-50 border-gray-100 opacity-50'
                              : 'p-3 bg-white border-gray-200 hover:border-[#233855]/20 hover:shadow-sm'
                          }`}
                        >
                          {isExpanded ? (
                            <div>
                              <p className="text-sm font-semibold text-[#233855] leading-snug mb-1">{item.title}</p>
                              {item.field && (
                                <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                                  <FiFolder size={9} /><span>{item.field}</span>
                                </div>
                              )}
                              {(item.points || (item.sources && item.sources.length > 0)) && (
                                <div className="flex flex-wrap items-center gap-1 mb-2.5">
                                  {item.points && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold">
                                      {String(item.points).replace(' pkt', '')} pkt
                                    </span>
                                  )}
                                  {item.sources && item.sources.map((src, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500">
                                      <FaGraduationCap className="text-[#233855]" size={7} />
                                      {src.replace('Zadanie', 'Zad.')}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="mb-3">
                                <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-1 flex items-center gap-1">
                                  <FaGraduationCap size={8} /> Przykładowe zadanie
                                </p>
                                <div className="text-[12px] text-gray-600 rounded-lg p-2.5 border bg-white border-gray-100">
                                  <RenderMarkdownWithLatex text={item.exampleProblem} />
                                </div>
                              </div>
                              {actionBtn && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onClose(); onStart(item); }}
                                  className={`w-full py-1.5 rounded-lg text-xs font-bold font-apotek tracking-wider uppercase active:scale-[0.98] transition-all ${actionBtn.cls}`}
                                >
                                  {actionBtn.label}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center text-center">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 text-[17px] ${isDim ? 'bg-gray-100/70 text-gray-300' : `bg-gray-50 ${cfg.colorClass}`}`}>
                                {cfg.icon}
                              </div>
                              <p className={`text-[13px] font-medium leading-tight line-clamp-2 ${isDim ? 'text-gray-400' : 'text-[#233855]'}`}>
                                {item.title}
                              </p>
                            </div>
                          )}
                        </motion.button>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

const PewniakiWidget = ({ stats, surefires, onOpenRoadmap }) => {
  const pct = stats.total > 0 ? Math.round((stats.mastered / stats.total) * 100) : 0;
  const isCompleted = stats.mastered === stats.total && stats.total > 0;

  return (
    <div
      onClick={onOpenRoadmap}
      className={`group relative w-full rounded-xl shadow-[0_12px_38px_-6px_rgba(35,56,85,0.30)] cursor-pointer transform transition-all duration-300 hover:scale-[1.01] hover:shadow-[0_22px_48px_-8px_rgba(35,56,85,0.36)] p-[1px] bg-gradient-to-br ${isCompleted ? 'from-[#E09F2E] via-[#F8AB3B] to-[#E09F2E]' : 'from-[#E09F2E]/50 via-[#233855]/50 to-[#E09F2E]/50'}`}
    >
      <div className="relative w-full bg-white rounded-[calc(0.75rem-1px)] overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl shadow-md text-white transition-colors duration-300 ${isCompleted ? 'bg-[#E09F2E]' : 'bg-[#233855]'}`}>
              <FiPackage className="text-xl text-[#F8AB3B]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-0.5">Kolekcja Pewniaków</p>
              <h3 className="text-xl font-bold font-apotek tracking-wide text-[#233855] leading-none">
                {stats.mastered} / {stats.total}
              </h3>
            </div>
          </div>
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
            <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
          </div>
        </div>

        {/* Pasek */}
        <div className="w-full h-3.5 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#FFD700] to-[#F8AB3B] shadow-[0_0_10px_rgba(248,171,59,0.3)] relative overflow-hidden transition-all duration-1000 ease-out"
            style={{ width: `${pct}%` }}
          >
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/40 to-transparent opacity-50" />
          </div>
        </div>

        {/* Kolekcja kropek */}
        <div className="flex flex-wrap gap-1 mt-3">
          {surefires.map(s => (
            <div
              key={s.id}
              className={`w-3 h-3 rounded-full border ${DOT_STATUS_COLOR[s.status] || DOT_STATUS_COLOR.NOT_STARTED}`}
            />
          ))}
        </div>
      </div>
      <div className={`absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-[0.03] ${isCompleted ? 'bg-[#E09F2E]' : 'bg-[#F8AB3B]'}`} />
      </div>
    </div>
  );
};


const SurefireAtomRow = ({ item, onStart }) => {
  const isWaiting  = item.status === 'WAITING';
  const isDecaying = item.status === 'DECAYING';
  const isPhase2Ready = item.status === 'PHASE2_READY';
  const isMastered = item.status === 'MASTERED';
  const isLocked   = item.is_locked;

  const getButtonConfig = () => {
    if (isLocked)     return { label: "Wymagane Premium", disabled: false, className: "bg-gray-100 text-gray-500 border border-gray-200 hover:bg-[#E09F2E]/10 hover:border-[#E09F2E]/30 hover:text-[#E09F2E]" };
    if (isWaiting)    return { label: `Wróć za ${item.waitTime}`, disabled: true, className: "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed" };
    if (isMastered)   return { label: "Powtórz", disabled: false, className: "bg-white text-[#007D00] border border-[#007D00]/30 hover:bg-[#007D00]/5" };
    if (isDecaying)   return { label: "Napraw teraz", disabled: false, className: "bg-[#8A0000] text-white hover:bg-[#8A0000]/90" };
    if (isPhase2Ready)return { label: "Utrwal", disabled: false, className: "bg-[#E09F2E] text-white hover:bg-[#E09F2E]/90" };
    return { label: "Start", disabled: false, className: "bg-[#233855] text-white hover:bg-[#233855]/90" };
  };
  const btnCfg = getButtonConfig();

  return (
    <div className="border-b border-gray-100 last:border-0 py-4">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-[#233855] leading-snug">{item.title}</p>
            {isLocked && <FaLock className="text-[#E09F2E] text-xs flex-shrink-0" />}
          </div>
          <div className="mt-1">
            <PhaseIndicator status={item.status} progress={item.progress} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
        <FiFolder size={10} />
        <span>{item.field}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold whitespace-nowrap">
          {String(item.points).replace(' pkt', '')} pkt
        </div>
        {item.sources && item.sources.map((src, idx) => (
          <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-[11px] text-gray-500 whitespace-nowrap">
            <FaGraduationCap className="text-[#233855]" size={8} />
            {src.replace("Zadanie", "Zad.")}
          </span>
        ))}
      </div>

      <div className="mb-3 relative">
        {isLocked && (
          <div className="absolute inset-0 bg-gray-50/80 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-lg">
            <FaLock className="text-gray-300 text-2xl" />
          </div>
        )}
        <p className="text-[11px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
          Przykładowe zadanie
        </p>
        <div className="p-2.5 bg-white rounded-lg text-sm text-gray-600 border border-gray-100 blur-[0.5px]">
          <RenderMarkdownWithLatex text={item.exampleProblem} />
        </div>
      </div>

      {isWaiting && (
        <div className="flex items-center gap-2 bg-[#233855]/5 rounded-lg px-3 py-2 mb-3 border border-[#233855]/10">
          <FiClock className="text-[#233855] stroke-[2.5] shrink-0" size={14} />
          <p className="text-[11px] text-[#233855] font-medium">
            Wróć za <span className="font-bold">{item.waitTime}</span>
          </p>
        </div>
      )}

      <button
        onClick={() => { if (!btnCfg.disabled) onStart(item); }}
        disabled={btnCfg.disabled}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold font-apotek tracking-wider uppercase transition-all active:scale-[0.98] ${btnCfg.className}`}
      >
        {isLocked ? <FaLock size={11} /> : (!btnCfg.disabled ? <FiPlay className="stroke-[2.5]" size={13} /> : <FiClock className="stroke-[2.5]" size={13} />)}
        {btnCfg.label}
      </button>

      <div className="flex items-start gap-1.5 mt-2 ">
        <FiInfo className="text-gray-300 stroke-[2] shrink-0 mt-[1px]" size={11} />
        <p className="text-xs text-gray-400 leading-relaxed">{STATUS_HINTS[item.status]}</p>
      </div>
    </div>
  );
};

const SurefireSheet = ({ stage, items, onClose, onStart }) => {
  const stageRef = useRef(stage);
  const itemsRef = useRef(items);

  useEffect(() => {
    if (stage) {
      window.dispatchEvent(new CustomEvent('bottomsheet:open'));
      return () => window.dispatchEvent(new CustomEvent('bottomsheet:close'));
    }
  }, [stage]);
  if (stage) { stageRef.current = stage; itemsRef.current = items; }
  const currentStage = stageRef.current;
  const currentItems = itemsRef.current || [];
  const cfg = currentStage ? SECTION_CONFIG[currentStage] : null;
  const dragControls = useDragControls();
  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(!!(stage && cfg));

  return ReactDOM.createPortal(
    <AnimatePresence>
      {stage && cfg && (
        <>
          <motion.div
            key="sf-backdrop"
            {...getSheetBackdropMotion()}
            className="fixed inset-0 bg-black/50 z-[9990]"
            onClick={onClose}
          />
          <motion.div
            key="sf-sheet"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-2xl lg:mx-auto lg:rounded-2xl"
          >
            <div
              className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>
            <div className="flex items-center gap-3 px-5 pt-2 pb-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex-1">
                <h2 className={`font-apotek tracking-[0.05em] text-[16px] uppercase leading-none ${cfg.colorClass}`}>
                  {cfg.label}
                </h2>
                <p className="text-xs text-gray-400 mt-1">{currentItems.length} {pluralPewniak(currentItems.length)}</p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <FiX size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-6 space-y-3">
              {currentItems.map(item => (
                <SurefireAtomRow key={item.id} item={item} onStart={onStart} />
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};



const STAGE_BORDER_COLOR = {
  decaying:     'border-[#8A0000]/30',
  consolidation:'border-[#E09F2E]/40',
  learning:     'border-[#233855]/25',
  waiting:      'border-gray-200',
  mastered:     'border-[#007D00]/30',
  not_started:  'border-gray-200',
};

const SurefireStageCard = ({ type, items, onOpen }) => {
  if (items.length === 0) return null;
  const cfg = SECTION_CONFIG[type];

  return (
    <button
      type="button"
      onClick={() => onOpen(type)}
      className={`w-full text-left p-3.5 mb-2.5 bg-white border ${STAGE_BORDER_COLOR[type]} rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.07)] hover:shadow-[0_4px_16px_rgba(35,56,85,0.12)] active:scale-[0.97] active:shadow-sm transition-all duration-150 group flex items-center gap-3`}
    >
      <div className="flex-shrink-0 p-2 rounded-xl shadow-md bg-[#233855]">
        {React.cloneElement(cfg.icon, { size: 15, className: "text-[#E09F2E]" })}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm text-[#233855] leading-snug truncate">
            {cfg.label}
          </span>
          {type === 'decaying' && (
            <span className="relative flex-shrink-0 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8A0000] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8A0000]" />
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400">
          {items.length} {pluralPewniak(items.length)} · {cfg.description.toLowerCase()}
        </p>
      </div>

      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
        <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
      </div>
    </button>
  );
};

const SurefiresScreen = () => {
  const navigate = useNavigate();
  const [surefires, setSurefires] = useState(() => readApiCache('surefire-atoms', Infinity) || []);
  const [loading, setLoading] = useState(!readApiCache('surefire-atoms', Infinity));
  const fetchGuard = useRef(false);

  const [selectedStage, setSelectedStage] = useState(null);
  const [isRoadmapOpen, setIsRoadmapOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(() => {
    const saved = localStorage.getItem('surefires_benefits_open');
    return saved !== null ? saved === 'true' : true;
  });

  // Loading overlay sesji (Orzeł + teksty, jak na Dashboardzie)
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isSessionDataReady, setIsSessionDataReady] = useState(false);
  const [sessionMessages, setSessionMessages] = useState(null);
  const sessionDataRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('surefires_benefits_open', String(isBenefitsOpen));
  }, [isBenefitsOpen]);

  useEffect(() => {
    if (fetchGuard.current) return;
    fetchGuard.current = true;

    const hasFreshCache = !!readApiCache('surefire-atoms');
    if (hasFreshCache) {
      setLoading(false);
      return;
    }
    const hasAnyData = !!readApiCache('surefire-atoms', Infinity);
    api.get('surefire-atoms/', hasAnyData ? { skipLoading: true } : {})
      .then(res => {
        setSurefires(res.data);
        writeApiCache('surefire-atoms', res.data);
      })
      .catch(err => console.error("Błąd pobierania pewniaków:", err))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => ({
    total: surefires.length,
    mastered: surefires.filter(s => s.status === 'MASTERED').length,
    decaying: surefires.filter(s => s.status === 'DECAYING').length,
    active: surefires.filter(s => ['DECAYING', 'PHASE2_READY', 'PHASE1_IN_PROGRESS'].includes(s.status)).length,
    remaining: surefires.filter(s => s.status === 'NOT_STARTED').length,
  }), [surefires]);

  const groupedItems = useMemo(() => ({
    decaying: surefires.filter(s => s.status === 'DECAYING'),
    consolidation: surefires.filter(s => s.status === 'PHASE2_READY'),
    learning: surefires.filter(s => s.status === 'PHASE1_IN_PROGRESS'),
    waiting: surefires.filter(s => s.status === 'WAITING'),
    mastered: surefires.filter(s => s.status === 'MASTERED'),
    not_started: surefires.filter(s => s.status === 'NOT_STARTED'),
  }), [surefires]);

  const handleStart = (item) => {
    if (item.is_locked) {
        setIsPaywallOpen(true);
        return;
    }
    setSessionMessages([`Ładujemy Pewniaka: ${item.title}`, ...LOADING_STEPS]);
    setIsSessionLoading(true);
    setIsSessionDataReady(false);
    sessionDataRef.current = null;
    api.post(`surefire-atoms/${item.id}/start/`, {}, { skipLoading: true })
      .then(res => {
        const { final_problems, user_elo_dict, atom_info } = res.data;
        if (!final_problems || final_problems.length === 0) {
          setIsSessionLoading(false);
          alert("Brak dostępnych zadań dla tego pewniaka.");
          return;
        }
        sessionDataRef.current = {
          final_problems,
          user_elo_dict,
          atomId: item.id,
          atomTitle: atom_info?.title,
          instruction: item.exampleProblem,
        };
        setIsSessionDataReady(true);
      })
      .catch(err => {
        console.error("Nie udało się rozpocząć pewniaka:", err);
        setIsSessionLoading(false);
      });
  };

  // Overlay zostaje do nawigacji — navigate odmontuje ekran razem z popupem w jednym commicie
  const handleSessionComplete = () => {
    const data = sessionDataRef.current;
    sessionDataRef.current = null;
    if (data && data.final_problems) {
      localStorage.removeItem("problemsSession");
      localStorage.setItem("problemsData", JSON.stringify(data.final_problems));
      localStorage.setItem("userEloDict", JSON.stringify(data.user_elo_dict));
      localStorage.setItem("currentSurefireAtom", JSON.stringify({
        id: data.atomId,
        title: data.atomTitle,
        instruction: data.instruction,
      }));
      navigate("/solve-surefire");
    } else {
      setIsSessionLoading(false);
      setIsSessionDataReady(false);
    }
  };

  const handleSubscribe = () => {
      setIsPaywallOpen(false);
      navigate("/upgrade");
  };

  const stageOrder = ['mastered', 'decaying', 'consolidation', 'learning', 'waiting', 'not_started'];


  return (
    <Page showNav={true}>
      <div className="bg-white lg:bg-transparent w-full">
      <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
      <div className="w-full lg:px-10 py-4 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200 flex flex-col gap-5">

          <div className="text-center">
            <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none">
              Pewniaki Maturalne
            </h1>
          </div>
          <div>
            <button
              type="button"
              onClick={() => setIsBenefitsOpen(v => !v)}
              className="flex items-center justify-between w-full mb-2 group"
            >
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">Dlaczego to działa?</span>
              <FiChevronDown className={`text-gray-300 group-hover:text-gray-500 transition-all duration-200 ${isBenefitsOpen ? 'rotate-180' : ''}`} size={14} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isBenefitsOpen ? 'max-h-52 opacity-100' : 'max-h-0 opacity-0'}`}>
              <SurefireBenefits />
            </div>
          </div>
          <PewniakiWidget
            stats={stats}
            surefires={surefires}
            onOpenRoadmap={() => setIsRoadmapOpen(true)}
          />

          {surefires.length === 0 && !loading ? (
            <div className="text-center py-10">
              <p className="text-gray-400">Brak pewniaków w bazie.</p>
            </div>
          ) : (
            <div>
              {stageOrder.map(type => (
                <SurefireStageCard
                  key={type}
                  type={type}
                  items={groupedItems[type]}
                  onOpen={setSelectedStage}
                />
              ))}
            </div>
          )}

          <SurefireSheet
            stage={selectedStage}
            items={selectedStage ? groupedItems[selectedStage] : []}
            onClose={() => setSelectedStage(null)}
            onStart={(item) => { setSelectedStage(null); handleStart(item); }}
          />
        </div>
        <div className="h-[13vh] flex-shrink-0" />
      </div>
      </div>
      <PewniakiRoadmapModal
        isOpen={isRoadmapOpen}
        onClose={() => setIsRoadmapOpen(false)}
        surefires={surefires}
        onStart={(item) => { setIsRoadmapOpen(false); handleStart(item); }}
      />
      <SurefirePaywallPopup
          isOpen={isPaywallOpen}
          onClose={() => setIsPaywallOpen(false)}
          onSubscribe={handleSubscribe}
      />

      <SessionSetupPopup
          isOpen={isSessionLoading}
          isDataReady={isSessionDataReady}
          onComplete={handleSessionComplete}
          messages={sessionMessages}
      />
    </Page>
  );
};

export default SurefiresScreen;